/**
 * The activity feed — "siste aktivitet (fra audit/event-log)" on the Overview page
 * (`chorus-byggeplan.md` Fase 1.4). Read from `audit_log`; see
 * `apps/api/src/audit/activity.service.ts` for why that table and not `event_log`.
 *
 * A file of its own rather than a corner of `notifications.ts`, because the two panels of the
 * Overview page are opposites. A notification is addressed to exactly one person and no admin
 * may read it (`r_notifications_recipient`, no override). An activity entry is the
 * organization's change log, and the matrix gives it to `superadmin` and `admin` alone —
 * `audit_log:list` / `read` / `export` are `no` for `member`, `limited` and `external`
 * (decisions 0001 and 0003). Filing an admin-only oversight contract inside a file named after
 * the most private table in the schema would be a mistake waiting to be copied.
 *
 * ---------------------------------------------------------------------------------------------
 * THIS FEED CARRIES WHO DID WHAT TO WHICH OBJECT. IT DOES NOT CARRY THE VALUES.
 *
 * `audit_log.old_values` and `audit_log.new_values` are deliberately **absent from this
 * contract**, and the read query never selects the columns at all. Four reasons, and the second
 * is the load-bearing one:
 *
 * 1. **They are a `jsonb` blob of unenumerable keys.** Principle 3 says every response goes
 *    through an explicit `toDto(row)` and never a spread. A column whose shape is "some subset
 *    of some other table's columns" cannot be written down field by field, so exposing it *is*
 *    the spread the principle forbids, wearing a column name.
 * 2. **`audit_log` is a different permission from the pages it records.** `message:list` is
 *    `part` for **every** role, superadmin included — nobody reads the messages of a thread they
 *    are not in — while `audit_log:list` is open to every admin. `apps/api/src/inbox` already
 *    had to redact `body` from the audit snapshot for exactly this reason. That fix is one
 *    table's `redact: ['body']`, maintained by hand; a feed that echoed values would depend on
 *    every future `auditSpec` remembering, and would republish whatever one of them forgot under
 *    a permission the original page never granted.
 * 3. **A delete carries the whole row.** `AuditedRepository.softDelete()` snapshots the row it
 *    is removing into `old_values` — every column of it. A feed that echoed diffs would hand
 *    back complete contact records, e-mail addresses and notes included, as a side effect of
 *    somebody having deleted one.
 * 4. **The field NAMES are safe and are enough.** `changed_fields` is a bounded vocabulary of
 *    column identifiers computed in one place by `app.write_audit()` — structure, not content.
 *    "Kari endret e-post og telefon på Nora Nilsen" is a useful line; "Kari endret e-post til
 *    nora@…" is a leak. `entityLabel` is the one human-readable value that survives, and it is a
 *    column the schema already designed to be shown: "Human-readable snapshot, e.g. the task
 *    title at the time".
 *
 * Reading the values of a change is a real need for compliance, and the matrix already has the
 * action for it — `audit_log:read`. When that endpoint is built it will be a deliberate,
 * separately-authorised act on one entry, with per-entity redaction decided on purpose. It is
 * not this feed, and it is not in this contract.
 * ---------------------------------------------------------------------------------------------
 *
 * WHAT IS NOT HERE, AND WHY. `actor_ip` and `actor_user_agent` are on the row and stay there:
 * they answer a forensic question, not "hva har skjedd i det siste", and putting a colleague's
 * IP address on a dashboard is surveillance bought for nothing. `request_id` and
 * `correlation_id` are internal tracing. Login and logout never appear at all, and not by a
 * filter written here — those rows carry `organization_id IS NULL`, so the tenant read policy
 * `p_audit_log_select` cannot see them from inside any organization.
 */
import { z } from 'zod';

import { actorTypeSchema, auditActionSchema } from '../enums.js';
import { cursorQuerySchema, page } from '../http/pagination.js';

/**
 * The one sort the feed offers: newest first, off `ix_audit_log__org_recent
 * (organization_id, occurred_at DESC, id DESC)`.
 *
 * The keyset carries **two** values, not one, and the second is not decoration.
 * `audit_log.occurred_at` is transaction start time, so every row written by one request shares
 * it to the microsecond — a single `PATCH` that retags a contact produces several. Without `id`
 * the cursor would land in the middle of that tie and skip the rest of it.
 */
export const ACTIVITY_SORT_KEYS = ['recent'] as const;
export type ActivitySortKey = (typeof ACTIVITY_SORT_KEYS)[number];

/**
 * Who acted, as the feed needs to render them.
 *
 * `type` is always present; the three person fields are not. `actorPersonId` is null for a
 * `system` or `service` actor — a retention sweep, a future import — and `contactId` /
 * `displayName` are null when a person acted but their contact card has since been soft-deleted.
 * The entry stays in the feed either way and the client renders an unknown-actor placeholder,
 * exactly as `TaskAssignee` does on the Tasks page. History does not disappear because somebody
 * left the organization.
 */
export const activityActorDtoSchema = z
  .object({
    type: actorTypeSchema,
    personId: z.uuid().nullable(),
    contactId: z.uuid().nullable(),
    displayName: z.string().nullable(),
    /** A11 (1031): personen var inne som support da det skjedde. Klienten skriver «(support)». */
    isSupport: z.boolean().optional(),
    /**
     * B3 (1071): handlingen kom med et token utenfra. Klienten skriver «<navn> via <klient>».
     * Utelatt når den kom fra Tvitvi selv.
     */
    client: z
      .object({ type: z.enum(['ai', 'integration']), name: z.string() })
      .optional(),
  })
  .meta({ id: 'ActivityActor' });

export const activityEntryDtoSchema = z
  .object({
    id: z.uuid(),
    occurredAt: z.iso.datetime(),
    action: auditActionSchema,
    actor: activityActorDtoSchema,
    /**
     * The physical table name, e.g. `tasks`. Text rather than an enum for the same reason the
     * column is: a Phase 2 table becomes auditable without a migration. The client renders it
     * through `entities.<entityType>`, so this is a translation key and not a visible string.
     */
    entityType: z.string(),
    entityId: z.uuid(),
    /** The object's name as it stood at the time, e.g. the task title. Null when it had none. */
    entityLabel: z.string().nullable(),
    /**
     * Which fields an `update` touched, in camelCase. Names only — never values; see the file
     * header. Empty for `create`, `delete` and the semantic actions, where "what changed" is the
     * action itself.
     */
    changedFields: z.array(z.string()),
  })
  .meta({ id: 'ActivityEntry' });

/**
 * The feed query: a page, and nothing else.
 *
 * `direction` is a literal for the same reason it is on the notification feed — an activity
 * panel is read from the newest end, and ascending would start the caller at the organization's
 * first ever audit row.
 *
 * THE OBJECT-HISTORY FILTER ARRIVED ON 30 AUGUST, and this header used to say why it had not:
 * "the indexes are there for the object-history view when a page needs one; adding the parameters
 * then is a contract change, not a migration." A page needed one — `PAGES.md` gives the contact's
 * detail page an "endringslogg" (sak 49) — so the parameters are here and the migration was
 * indeed unnecessary: `ix_audit_log__org_entity` is
 * `(organization_id, entity_type, entity_id, occurred_at DESC, id DESC)`, which is this query's
 * quals and this query's order, column for column.
 *
 * BOTH OR NEITHER. `entityId` without `entityType` would be a uuid compared across every table in
 * the organisation — the index could not serve it, and two different objects could in principle
 * share an id. The refinement makes the pair the unit rather than leaving it to each caller.
 *
 * `actorPersonId` is STILL absent, for the reason the whole paragraph above used to give: nothing
 * asks "everything Kari has ever done", and `ix_audit_log__org_actor` will still be there the day
 * something does.
 */
export const listActivityQuerySchema = cursorQuerySchema(ACTIVITY_SORT_KEYS)
  .extend({
    direction: z.literal('desc').default('desc'),
    /** Which table the object lives in — the audited row's own `entity_type`. */
    entityType: z.string().trim().min(1).max(64).optional(),
    entityId: z.uuid().optional(),
    /**
     * SPILLESTEDETS «SISTE AKTIVITET I HUSET» (sak 229): endringer på et utvalg tabeller —
     * arrangementene, salene, datoforespørslene — uten ett bestemt objekt. `?entityTypes=a,b` eller
     * gjentatt. Kan ikke kombineres med objekthistorikken over; det er to forskjellige spørsmål.
     */
    entityTypes: z
      .preprocess(
        (value) => {
          if (value === undefined || value === null) return undefined;
          if (Array.isArray(value)) return value;
          const text = String(value).trim();
          return text.length === 0 ? undefined : text.split(',');
        },
        z.array(z.string().trim().min(1).max(64)).min(1).max(10),
      )
      .optional(),
  })
  .refine((query) => (query.entityType === undefined) === (query.entityId === undefined), {
    message: 'entityType and entityId must be sent together.',
    path: ['entityId'],
  })
  .refine((query) => query.entityTypes === undefined || query.entityType === undefined, {
    message: 'entityTypes cannot be combined with entityType.',
    path: ['entityTypes'],
  })
  .meta({ id: 'ListActivityQuery' });

export const activityListResponseSchema = page(activityEntryDtoSchema, 'ActivityListResponse');

export type ActivityActorDto = z.infer<typeof activityActorDtoSchema>;
export type ActivityEntryDto = z.infer<typeof activityEntryDtoSchema>;
export type ListActivityQuery = z.infer<typeof listActivityQuerySchema>;
export type ActivityListResponse = z.infer<typeof activityListResponseSchema>;
